/**
 * Direct Chat file upload - pure policy (ENG-7223).
 *
 * A human chatting with an agent in the Direct Chat console can attach files.
 * The browser PUTs bytes straight to a private S3 bucket via an API-minted
 * presigned URL (bytes never transit the chat channel), mirroring the
 * Augmented Live `upload_asset` flow. This module is the framework- and
 * runtime-agnostic policy half: the allowlist, the object-key derivation, the
 * declared-claim validation, and the magic-byte verification. All S3 I/O lives
 * in `packages/api/src/lib/s3.ts`; nothing here imports `@aws-sdk` so the module
 * stays browser-safe (the webapp composer reuses the allowlist + cap).
 *
 * Object layout (content-addressed, per-agent prefix):
 *   {agentId}/{sha256}.{ext}
 *
 * Keyed under the {agentId}/ prefix so cross-agent reads are a prefix boundary
 * and a future per-agent erasure is a single sweep. The sha256 is the file's
 * content hash so re-uploading identical bytes is idempotent.
 *
 * SECURITY (council ENG-7223): the presigned PUT means the server never sees the
 * bytes at upload time, so the declared content-type / byte-size / sha256 are all
 * attacker-controlled claims. `validateDirectChatUpload` only screens the *claim*
 * (cheap, pre-presign). The real boundary is `verifyDirectChatUploadBytes`, run
 * server-side at link time against the actual object bytes - it re-derives the
 * type from magic bytes and rejects a file whose real type doesn't match its
 * declared one (e.g. an HTML/SVG polyglot uploaded as `image/png`). image/svg+xml
 * and text/html are intentionally NOT on the allowlist: SVG/HTML can carry active
 * content, and these attachments are rendered as thumbnails / handed to an agent.
 * Documents (pdf/txt/csv/docx/xlsx) are always served `Content-Disposition:
 * attachment`, never inline, so even a mislabelled doc cannot execute in a viewer.
 */

import { sniffImageType } from '../integrations/augmented-live/asset.js';

const MB = 1024 * 1024;

/** Single per-file byte cap for Direct Chat uploads (v1). */
export const DIRECT_CHAT_UPLOAD_MAX_BYTES = 10 * MB;

export type DirectChatUploadKind = 'image' | 'document';

export interface DirectChatUploadTypeSpec {
  /** Canonical file extension for the S3 key (no dot). */
  ext: string;
  /** Whether this type renders inline as a thumbnail (images) or as a download chip (documents). */
  kind: DirectChatUploadKind;
}

/**
 * Content-type allowlist. Images render inline as thumbnails; documents render as
 * a download chip and are always served `Content-Disposition: attachment`.
 * image/svg+xml and text/html are deliberately absent (see the module header).
 */
export const DIRECT_CHAT_UPLOAD_TYPES: Readonly<Record<string, DirectChatUploadTypeSpec>> = {
  'image/png': { ext: 'png', kind: 'image' },
  'image/jpeg': { ext: 'jpg', kind: 'image' },
  'image/webp': { ext: 'webp', kind: 'image' },
  'image/gif': { ext: 'gif', kind: 'image' },
  'application/pdf': { ext: 'pdf', kind: 'document' },
  'text/plain': { ext: 'txt', kind: 'document' },
  'text/csv': { ext: 'csv', kind: 'document' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    ext: 'docx',
    kind: 'document',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    ext: 'xlsx',
    kind: 'document',
  },
};

const SHA256_HEX_RE = /^[0-9a-f]{64}$/;

/** Lowercase, trim a declared content-type for allowlist lookup. */
export function normalizeUploadContentType(contentType: string): string {
  return (contentType ?? '').trim().toLowerCase();
}

/** The set of allowed content-types, for a client-side `accept` attribute / messages. */
export function allowedUploadContentTypes(): string[] {
  return Object.keys(DIRECT_CHAT_UPLOAD_TYPES);
}

export type DirectChatUploadValidationError = {
  ok: false;
  code: 'unsupported_type' | 'too_large' | 'bad_checksum';
  message: string;
};

export interface DirectChatUploadValidationOk {
  ok: true;
  /** Canonical extension for the key. */
  ext: string;
  /** Normalized (trimmed, lowercased) content-type to pin in the presigned PUT. */
  contentType: string;
  kind: DirectChatUploadKind;
}

/**
 * Validate the upload *claim* (declared content-type + size + checksum shape).
 * Cheap and runs both client-side (pre-flight) and server-side (pre-presign).
 * This does NOT prove the bytes match the claim - `verifyDirectChatUploadBytes`
 * is the real boundary, run after the upload against the stored object.
 */
export function validateDirectChatUpload(input: {
  contentType: string;
  byteSize: number;
  sha256: string;
}): DirectChatUploadValidationOk | DirectChatUploadValidationError {
  const contentType = normalizeUploadContentType(input.contentType);
  const spec = DIRECT_CHAT_UPLOAD_TYPES[contentType];
  if (!spec) {
    return {
      ok: false,
      code: 'unsupported_type',
      message: `Unsupported content_type "${input.contentType}". Allowed: ${allowedUploadContentTypes().join(', ')}.`,
    };
  }
  if (!SHA256_HEX_RE.test(input.sha256 ?? '')) {
    return {
      ok: false,
      code: 'bad_checksum',
      message: 'content_sha256 must be a lowercase hex SHA-256 (64 chars) of the file bytes.',
    };
  }
  if (!Number.isInteger(input.byteSize) || input.byteSize <= 0) {
    return { ok: false, code: 'too_large', message: 'byte_size must be a positive integer number of bytes.' };
  }
  if (input.byteSize > DIRECT_CHAT_UPLOAD_MAX_BYTES) {
    return {
      ok: false,
      code: 'too_large',
      message: `File is ${input.byteSize} bytes; the limit is ${DIRECT_CHAT_UPLOAD_MAX_BYTES} bytes (10 MB).`,
    };
  }
  return { ok: true, ext: spec.ext, contentType, kind: spec.kind };
}

/** S3 key for a content-addressed upload under its agent's prefix. */
export function directChatUploadKey(agentId: string, sha256: string, ext: string): string {
  return `${agentId}/${sha256}.${ext}`;
}

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d]; // "%PDF-"
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]; // "PK\x03\x04" - OOXML docx/xlsx container

function startsWith(bytes: Uint8Array, sig: number[]): boolean {
  if (bytes.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[i] !== sig[i]) return false;
  }
  return true;
}

/** True if the ASCII needle appears within the first `limit` bytes of the buffer. */
function containsAscii(bytes: Uint8Array, needle: string, limit = 8192): boolean {
  const sig = Array.from(needle, (ch) => ch.charCodeAt(0));
  const end = Math.min(bytes.length, limit) - sig.length;
  for (let i = 0; i <= end; i++) {
    let match = true;
    for (let j = 0; j < sig.length; j++) {
      if (bytes[i + j] !== sig[j]) {
        match = false;
        break;
      }
    }
    if (match) return true;
  }
  return false;
}

/**
 * Reject bytes that carry a known *binary* signature when a text type is claimed.
 * Text files (txt/csv) have no positive magic-byte signature, so we verify
 * negatively: the bytes must not be an image/pdf/zip in disguise, and must not
 * contain a NUL in the leading sample (a strong binary tell). Combined with
 * always serving text as `Content-Disposition: attachment`, this keeps a polyglot
 * out of the text lane.
 */
function looksLikeText(bytes: Uint8Array): boolean {
  if (sniffImageType(bytes) !== null) return false;
  if (startsWith(bytes, PDF_MAGIC) || startsWith(bytes, ZIP_MAGIC)) return false;
  const sample = bytes.subarray(0, Math.min(bytes.length, 4096));
  for (let i = 0; i < sample.length; i++) {
    if (sample[i] === 0x00) return false;
  }
  return true;
}

export type DirectChatVerifyResult =
  | { ok: true; contentType: string; ext: string; kind: DirectChatUploadKind }
  | { ok: false; reason: string };

/**
 * Verify the stored object's leading bytes against its declared content-type.
 * This is the security boundary: it runs server-side at link time over the real
 * object bytes (a ranged GET) and rejects any file whose magic bytes don't match
 * the allowed declared type. Images must sniff to the exact declared image type;
 * PDFs must start with `%PDF-`; docx/xlsx must be a ZIP/OOXML container; txt/csv
 * must look like text (no binary signature, no NULs). Anything else is rejected.
 */
export function verifyDirectChatUploadBytes(
  bytes: Uint8Array,
  declaredContentType: string,
): DirectChatVerifyResult {
  const contentType = normalizeUploadContentType(declaredContentType);
  const spec = DIRECT_CHAT_UPLOAD_TYPES[contentType];
  if (!spec) return { ok: false, reason: `content_type "${declaredContentType}" is not allowed` };

  if (spec.kind === 'image') {
    const real = sniffImageType(bytes);
    if (real !== contentType) {
      return {
        ok: false,
        reason: `declared ${contentType} but the bytes are ${real ?? 'not a recognized image'}`,
      };
    }
    return { ok: true, contentType, ext: spec.ext, kind: spec.kind };
  }

  if (contentType === 'application/pdf') {
    if (!startsWith(bytes, PDF_MAGIC)) return { ok: false, reason: 'declared application/pdf but the bytes are not a PDF' };
    return { ok: true, contentType, ext: spec.ext, kind: spec.kind };
  }

  if (spec.ext === 'docx' || spec.ext === 'xlsx') {
    // OOXML files are ZIP containers, but not every ZIP is an OOXML document.
    // Require both the ZIP signature AND the mandatory `[Content_Types].xml`
    // part (stored as an uncompressed filename near the archive start, so it
    // appears verbatim in the leading bytes). This rejects an arbitrary ZIP
    // declared as docx/xlsx. The leading bytes still can't distinguish docx from
    // xlsx (that needs the central directory); the declared sub-type sets the
    // extension only, and the file is served as an attachment regardless.
    if (!startsWith(bytes, ZIP_MAGIC)) {
      return { ok: false, reason: `declared ${contentType} but the bytes are not an OOXML (ZIP) container` };
    }
    if (!containsAscii(bytes, '[Content_Types].xml')) {
      return { ok: false, reason: `declared ${contentType} but the ZIP is not a valid OOXML document` };
    }
    return { ok: true, contentType, ext: spec.ext, kind: spec.kind };
  }

  // text/plain, text/csv
  if (!looksLikeText(bytes)) {
    return { ok: false, reason: `declared ${contentType} but the bytes look binary` };
  }
  return { ok: true, contentType, ext: spec.ext, kind: spec.kind };
}
